/**
 * image-grid-picker.js
 *
 * Generic image-grid accordion picker for Horizon theme.
 *
 * Works for ANY product option configured as "image_grid" in block settings —
 * birthstones, chain lengths, metal finishes, engravings, etc.
 *
 * Swatch visual resolution order (highest priority wins):
 *   1. product metafield  custom.option_images  → real photo URL
 *   2. product metafield  custom.option_colors  → custom hex
 *   3. BUILT_IN_DEFAULTS                        → built-in hex map (birthstones etc.)
 *
 * Horizon integration:
 *   Clicks on custom buttons trigger the matching hidden radio input via a
 *   native 'change' event. Horizon's VariantPicker component receives it and
 *   handles price, availability, URL history, and morph as normal.
 *
 *   After morph completes Horizon dispatches 'variant:update' (VariantUpdateEvent)
 *   on the <variant-picker> element. We listen for that event and re-paint all
 *   swatches + restore the selected-state classes, because morph wipes inline
 *   styles and resets the DOM to whatever Liquid rendered (no JS state).
 */

'use strict';

// ─────────────────────────────────────────────────────────────────────────────
// Built-in colour defaults — fallback of last resort when no metafield image
// or colour is defined. Extend freely; keys are month names lowercased.
// ─────────────────────────────────────────────────────────────────────────────
const BUILT_IN_DEFAULTS = {
  january:   '#9B1B30',
  february:  '#9B59B6',
  march:     '#7EC8C8',
  april:     '#E8F4FD',
  may:       '#228B22',
  june:      '#EAE0C8',
  july:      '#E0115F',
  august:    '#AACC44',
  september: '#1F4FAB',
  october:   '#E8A0BF',
  november:  '#F5A623',
  december:  '#40C4AA',
};

const MONTHS = Object.keys(BUILT_IN_DEFAULTS);

function monthFromValue(value) {
  const lower = value.toLowerCase();
  return MONTHS.find((m) => lower.includes(m)) ?? null;
}

function builtInColor(value) {
  const month = monthFromValue(value);
  return month ? BUILT_IN_DEFAULTS[month] : null;
}

// ─────────────────────────────────────────────────────────────────────────────
// ImageGridPicker
// ─────────────────────────────────────────────────────────────────────────────
class ImageGridPicker {
  /** @param {HTMLElement} root  The [data-igp] element */
  constructor(root) {
    this.root = root;

    // Parse metafield JSON embedded as data attributes by Liquid.
    // Shape: { "Option Name": { "Option Value": "https://..." } }
    this.metaImages = this._parseJson(root.dataset.igpImages);
    this.metaColors = this._parseJson(root.dataset.igpColors);

    this._paintAllSwatches();
    this._bindAccordions();
    this._bindOptionButtons();
    this._bindPostMorphRepaint();
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  _parseJson(raw) {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (e) {
      console.warn('[ImageGridPicker] Failed to parse JSON:', e);
      return null;
    }
  }

  /**
   * Resolve the best visual for an option name + value combination.
   * @returns {{ type: 'image'|'color', value: string } | null}
   */
  _resolveVisual(optionName, optionValue) {
    // 1. Metafield image URL
    const imgUrl = this.metaImages?.[optionName]?.[optionValue];
    if (imgUrl) return { type: 'image', value: imgUrl };

    // 2. Metafield custom hex
    const metaHex = this.metaColors?.[optionName]?.[optionValue];
    if (metaHex) return { type: 'color', value: metaHex };

    // 3. Built-in month colour
    const defaultHex = builtInColor(optionValue);
    if (defaultHex) return { type: 'color', value: defaultHex };

    return null;
  }

  _applyVisual(el, visual) {
    if (!el || !visual) return;
    if (visual.type === 'image') {
      el.style.backgroundImage = `url('${visual.value}')`;
      el.style.backgroundColor = '';
    } else {
      el.style.backgroundColor = visual.value;
      el.style.backgroundImage = '';
    }
  }

  // ── 1. Paint swatches ──────────────────────────────────────────────────────

  /**
   * Paint every swatch circle in the grid and every accordion header preview.
   * Called on init and again after every morph via _bindPostMorphRepaint.
   */
  _paintAllSwatches() {
    this.root.querySelectorAll('[data-igp-swatch]').forEach((swatch) => {
      const btn = swatch.closest('[data-igp-option-btn]');
      if (!btn) return;
      const visual = this._resolveVisual(
        btn.dataset.igpOptionName ?? '',
        btn.dataset.igpValue ?? ''
      );
      this._applyVisual(swatch, visual);
    });

    this._paintAllPreviews();
  }

  _paintAllPreviews() {
    this.root.querySelectorAll('[data-igp-preview]').forEach((preview) => {
      const visual = this._resolveVisual(
        preview.dataset.igpOptionName ?? '',
        preview.dataset.igpSelectedValue ?? ''
      );
      this._applyVisual(preview, visual);
    });
  }

  // ── 2. Restore selected-state classes after morph ─────────────────────────

  /**
   * After Horizon morphs the picker HTML, the custom is-selected classes are
   * gone because Liquid doesn't know about JS selections. We re-derive the
   * correct selected state by reading which native radio inputs are checked
   * (the native form state IS preserved by morph via data-current-checked).
   */
  _restoreSelectedStates() {
    this.root.querySelectorAll('.igp__native input[type="radio"]').forEach((radio) => {
      // Use data-current-checked rather than .checked because morph restores
      // data attributes reliably but the .checked property may lag.
      const isChecked = radio.dataset.currentChecked === 'true';
      if (!isChecked) return;

      const fieldsetIndex = radio.dataset.fieldsetIndex;
      const inputIndex    = radio.dataset.inputIndex;
      if (fieldsetIndex === undefined || inputIndex === undefined) return;

      // Deselect all custom buttons in this fieldset
      this.root
        .querySelectorAll(`[data-igp-option-btn][data-fieldset-index="${fieldsetIndex}"]`)
        .forEach((btn) => {
          btn.classList.remove('is-selected');
          btn.setAttribute('aria-pressed', 'false');
        });

      // Select the matching custom button
      const matchingBtn = this.root.querySelector(
        `[data-igp-option-btn][data-fieldset-index="${fieldsetIndex}"][data-input-index="${inputIndex}"]`
      );
      if (!matchingBtn) return;

      matchingBtn.classList.add('is-selected');
      matchingBtn.setAttribute('aria-pressed', 'true');

      const selectedValue = matchingBtn.dataset.igpValue ?? '';
      const optionName    = matchingBtn.dataset.igpOptionName ?? '';

      // Update accordion header label + preview swatch
      const accordion = this.root.querySelector(
        `[data-igp-accordion][data-fieldset-index="${fieldsetIndex}"]`
      );
      if (accordion) {
        const label = accordion.querySelector('[data-igp-selected-label]');
        if (label) label.textContent = selectedValue;

        const preview = accordion.querySelector('[data-igp-preview]');
        if (preview) {
          preview.dataset.igpSelectedValue = selectedValue;
          this._applyVisual(preview, this._resolveVisual(optionName, selectedValue));
        }
      }

      // Update pill-button label for non-grid options
      const btnLabel = this.root.querySelector(
        `[data-igp-btn-label][data-fieldset-index="${fieldsetIndex}"]`
      );
      if (btnLabel) btnLabel.textContent = selectedValue;
    });
  }

  // ── 3. Post-morph repaint ──────────────────────────────────────────────────

  /**
   * Listen for Horizon's VariantUpdateEvent on the parent <variant-picker>.
   *
   * This is the key fix for blank swatches after gem selection:
   *   1. Customer clicks a gem → _selectOption() → _fireNativeChange()
   *   2. Horizon fetches updated section HTML
   *   3. Horizon morphs the <variant-picker> innerHTML
   *      → inline styles on swatch circles are wiped (fresh HTML has none)
   *      → is-selected classes are wiped (Liquid doesn't set them)
   *   4. Horizon dispatches 'variant:update' on <variant-picker>
   *   5. We catch it here → re-paint all swatches → restore selected states
   *
   * Event name: 'variant:update'
   * Defined in: @theme/events  (VariantUpdateEvent)
   * Dispatched by: VariantPicker.fetchUpdatedSection() in variant-picker.js
   */
  _bindPostMorphRepaint() {
    const variantPicker = this.root.closest('variant-picker');
    if (!variantPicker) return;

    variantPicker.addEventListener('variant:update', () => {
      // Re-read metafield data from the root element's data attributes.
      // These survive morph because they live on [data-igp] which is inside
      // the variant-picker but Horizon's morph algorithm preserves attribute
      // values on nodes it reuses rather than replacing.
      this.metaImages = this._parseJson(this.root.dataset.igpImages);
      this.metaColors = this._parseJson(this.root.dataset.igpColors);

      this._paintAllSwatches();
      this._restoreSelectedStates();
    });
  }

  // ── 4. Accordion open / close ──────────────────────────────────────────────

  _bindAccordions() {
    this.root.querySelectorAll('[data-igp-toggle]').forEach((toggle) => {
      toggle.addEventListener('click', () => {
        const accordion = toggle.closest('[data-igp-accordion]');
        if (!accordion) return;
        const nowOpen = !accordion.classList.contains('is-open');
        accordion.classList.toggle('is-open', nowOpen);
        toggle.setAttribute('aria-expanded', String(nowOpen));
      });
    });
  }

  // ── 5. Option button clicks ────────────────────────────────────────────────

  _bindOptionButtons() {
    this.root.querySelectorAll('[data-igp-option-btn]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.getAttribute('aria-disabled') === 'true') return;
        const fieldsetIndex = parseInt(btn.dataset.fieldsetIndex ?? '-1', 10);
        const inputIndex    = parseInt(btn.dataset.inputIndex ?? '-1', 10);
        if (fieldsetIndex < 0 || inputIndex < 0) return;
        this._selectOption(btn, fieldsetIndex, inputIndex);
      });
    });
  }

  _selectOption(clickedBtn, fieldsetIndex, inputIndex) {
    const optionName  = clickedBtn.dataset.igpOptionName ?? '';
    const optionValue = clickedBtn.dataset.igpValue ?? '';

    // Deselect all siblings in this fieldset
    this.root
      .querySelectorAll(`[data-igp-option-btn][data-fieldset-index="${fieldsetIndex}"]`)
      .forEach((sib) => {
        sib.classList.remove('is-selected');
        sib.setAttribute('aria-pressed', 'false');
      });

    // Select the clicked button
    clickedBtn.classList.add('is-selected');
    clickedBtn.setAttribute('aria-pressed', 'true');

    // Update pill-button selected label
    const btnLabel = this.root.querySelector(
      `[data-igp-btn-label][data-fieldset-index="${fieldsetIndex}"]`
    );
    if (btnLabel) btnLabel.textContent = optionValue;

    // Update accordion header: label + preview swatch
    const accordion = this.root.querySelector(
      `[data-igp-accordion][data-fieldset-index="${fieldsetIndex}"]`
    );
    if (accordion) {
      const label = accordion.querySelector('[data-igp-selected-label]');
      if (label) label.textContent = optionValue;

      const preview = accordion.querySelector('[data-igp-preview]');
      if (preview) {
        preview.dataset.igpSelectedValue = optionValue;
        this._applyVisual(preview, this._resolveVisual(optionName, optionValue));
      }

      // Accordion stays open after selection so the customer can see
      // their choice and change it if needed. They tap the header to close.
    }

    // Fire the native radio change → triggers Horizon's VariantPicker flow
    this._fireNativeChange(fieldsetIndex, inputIndex);
  }

  _fireNativeChange(fieldsetIndex, inputIndex) {
    const nativeFieldset = this.root.querySelector(
      `.igp__native [data-fieldset-index="${fieldsetIndex}"]`
    );

    if (!nativeFieldset) {
      console.warn('[ImageGridPicker] native fieldset not found for index', fieldsetIndex);
      return;
    }

    const inputs      = Array.from(nativeFieldset.querySelectorAll('input[type="radio"]'));
    const nativeInput = inputs[inputIndex];

    if (!nativeInput) {
      console.warn('[ImageGridPicker] native input not found at index', inputIndex);
      return;
    }

    if (nativeInput.checked) return;

    nativeInput.checked = true;
    nativeInput.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Initialisation
// ─────────────────────────────────────────────────────────────────────────────

function initPickers() {
  document.querySelectorAll('[data-igp]').forEach((root) => {
    if (root._igpInstance) return;
    root._igpInstance = new ImageGridPicker(root);
  });
}

document.addEventListener('DOMContentLoaded', initPickers);

// Theme Editor section reloads — clear cached instances so they fully re-init
document.addEventListener('shopify:section:load', () => {
  document.querySelectorAll('[data-igp]').forEach((root) => {
    delete root._igpInstance;
  });
  initPickers();
});

// MutationObserver: catches cases where the [data-igp] root itself is replaced
// (e.g. quick-add modal injection, full section swap)
const _observer = new MutationObserver(() => {
  document.querySelectorAll('[data-igp]').forEach((root) => {
    if (!root._igpInstance) {
      root._igpInstance = new ImageGridPicker(root);
    }
  });
});

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('variant-picker').forEach((vp) => {
    _observer.observe(vp, { childList: true, subtree: true });
  });
});
